import React from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RoleBadge, UserStatusBadge } from "@/components/shared/Badges";

export default function SettingsPage() {
    const { user, logout } = useAuth();
    const {
        register,
        handleSubmit,
        reset,
        formState: { isSubmitting, errors },
    } = useForm();

    const canChangePw = user?.role !== "SHOP_OWNER"; // shop owners use Google

    const submit = async (v) => {
        if (v.new_password !== v.confirm) {
            toast.error("New passwords do not match");
            return;
        }
        try {
            await api.post("/auth/change-password", {
                old_password: v.old_password,
                new_password: v.new_password,
            });
            toast.success("Password updated. Please log in again.");
            reset();
            setTimeout(logout, 800);
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };

    return (
        <div className="space-y-6" data-testid="settings-page">
            <header>
                <div className="text-sm font-medium text-orange-600">Account</div>
                <h1 className="mt-1 text-3xl font-extrabold text-neutral-900 sm:text-4xl">
                    Settings
                </h1>
            </header>

            <section className="rounded-2xl border border-neutral-200 bg-white p-6">
                <h2 className="text-lg font-semibold text-neutral-900">Profile</h2>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    <Info label="Full name" value={user?.full_name} />
                    <Info label="Email" value={user?.email} />
                    <div>
                        <div className="text-xs uppercase tracking-wider text-neutral-500">
                            Role
                        </div>
                        <div className="mt-1">
                            <RoleBadge role={user?.role} />
                        </div>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-wider text-neutral-500">
                            Status
                        </div>
                        <div className="mt-1">
                            <UserStatusBadge status={user?.status} />
                        </div>
                    </div>
                    {user?.district && <Info label="District" value={user.district} className="capitalize" />}
                    {user?.business_name && <Info label="Business" value={user.business_name} />}
                    {user?.mobile_number && <Info label="Mobile" value={user.mobile_number} />}
                </div>
            </section>

            {canChangePw && (
                <section className="rounded-2xl border border-neutral-200 bg-white p-6">
                    <h2 className="text-lg font-semibold text-neutral-900">
                        Change password
                    </h2>
                    <form onSubmit={handleSubmit(submit)} className="mt-4 grid gap-4 sm:max-w-md" data-testid="change-password-form">
                        <div>
                            <Label>Current password</Label>
                            <Input
                                type="password"
                                {...register("old_password", { required: true })}
                                data-testid="cp-old"
                            />
                        </div>
                        <div>
                            <Label>New password (min 8 chars)</Label>
                            <Input
                                type="password"
                                {...register("new_password", { required: true, minLength: 8 })}
                                data-testid="cp-new"
                            />
                            {errors.new_password && (
                                <p className="mt-1 text-xs text-rose-600">
                                    Min 8 characters
                                </p>
                            )}
                        </div>
                        <div>
                            <Label>Confirm new password</Label>
                            <Input
                                type="password"
                                {...register("confirm", { required: true, minLength: 8 })}
                                data-testid="cp-confirm"
                            />
                        </div>
                        <div>
                            <Button
                                type="submit"
                                disabled={isSubmitting}
                                className="nest-gradient text-white"
                                data-testid="cp-submit"
                            >
                                {isSubmitting ? "Saving…" : "Update password"}
                            </Button>
                        </div>
                    </form>
                </section>
            )}
        </div>
    );
}

const Info = ({ label, value, className }) => (
    <div>
        <div className="text-xs uppercase tracking-wider text-neutral-500">
            {label}
        </div>
        <div className={`mt-1 text-sm font-medium text-neutral-900 ${className || ""}`}>
            {value || "—"}
        </div>
    </div>
);
